import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useCompanyPrice1Min } from './useCompanyPrice1Min';
import { useCompanyPrice15Min } from './useCompanyPrice15Min';
import { useCompanyPrice30Min } from './useCompanyPrice30Min';
import { useCompanyPrice4Hour } from './useCompanyPrice4Hour';
import { useCompanyPriceEod } from './useCompanyPriceEod';
import {
  StockPriceDataResult,
  TimeRange,
  TimeRangeQueryResult,
  RawPriceRecord,
} from '../types/stockChart';

/**
 * Calculate date ranges for each time period.
 * Memoized to prevent unnecessary recalculations.
 */
const calculateDateRanges = () => {
  const now = dayjs();
  const yearStart = now.startOf('year');
  return {
    '1D': {
      from: now.format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '1W': {
      from: now.subtract(7, 'day').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '1M': {
      from: now.subtract(1, 'month').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '3M': {
      from: now.subtract(3, 'month').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '6M': {
      from: now.subtract(6, 'month').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    'YTD': {
      from: yearStart.format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '1Y': {
      from: now.subtract(1, 'year').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '5Y': {
      from: now.subtract(5, 'year').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
    '10Y': {
      from: now.subtract(10, 'year').format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    },
  };
};

/**
 * Determine the appropriate resolution for YTD based on days elapsed since Jan 1.
 * Uses higher resolution for shorter periods, lower for longer.
 */
type YTDResolution = '15min' | '30min' | '4hour' | 'daily';

const getYTDResolution = (): YTDResolution => {
  const now = dayjs();
  const yearStart = now.startOf('year');
  const daysSinceYearStart = now.diff(yearStart, 'day');

  if (daysSinceYearStart <= 7) return '15min';      // First week: use 15-min
  if (daysSinceYearStart <= 31) return '30min';     // First month: use 30-min
  if (daysSinceYearStart <= 180) return '4hour';    // Up to 6 months: use 4-hour
  return 'daily';                                    // After 6 months: use daily EOD
};

/**
 * Normalize a query result to the common TimeRangeQueryResult interface.
 * Handles different response shapes from various API endpoints.
 */
const normalizeQuery = (
  query: {
    data?: { data?: RawPriceRecord[] } | RawPriceRecord[];
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  },
  metadataExtractor?: (data: any) => TimeRangeQueryResult['metadata']
): TimeRangeQueryResult => {
  // Extract the data array from the response
  // API returns { data: [...], metadata: {...} }
  // We need just the data array
  let rawData: RawPriceRecord[] | undefined;

  if (query.data) {
    if (Array.isArray(query.data)) {
      rawData = query.data;
    } else if ('data' in query.data && Array.isArray(query.data.data)) {
      rawData = query.data.data;
    }
  }

  return {
    data: rawData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    metadata: metadataExtractor ? metadataExtractor(query.data) : undefined,
  };
};

/**
 * Unified hook for fetching stock price data across all time ranges.
 *
 * Key behaviors:
 * - ALL hooks are called unconditionally (no rules of hooks violations)
 * - All queries fire in parallel immediately (eager loading)
 * - Each time range has independent loading/error states
 * - React Query handles caching - switching tabs is instant after first load
 *
 * @param ticker - Stock ticker symbol
 * @param ranges - The ranges to fetch; every other query stays mounted but
 *   disabled (a chart restricted to 1M and 1Y makes two requests, not eight)
 * @returns Object mapping each TimeRange to its query result
 */
export const useStockPriceData = (
  ticker: string,
  ranges?: readonly TimeRange[]
): StockPriceDataResult => {
  // Pre-calculate all date ranges (memoized)
  const dateRanges = useMemo(() => calculateDateRanges(), []);
  const wanted = (range: TimeRange) => ranges === undefined || ranges.includes(range);

  // =====================================================
  // ALL hooks called unconditionally
  // React Query handles parallel fetching and caching
  // =====================================================

  // 1D - Uses 1-minute data (existing endpoint)
  const q1D = useCompanyPrice1Min(wanted('1D') ? ticker : undefined);

  // 1W - Uses 15-minute data for higher resolution
  const q1W = useCompanyPrice15Min(ticker, dateRanges['1W'].from, dateRanges['1W'].to, wanted('1W'));

  // 1M - Uses 30-minute data for monthly view (higher resolution)
  const q1M = useCompanyPrice30Min(ticker, dateRanges['1M'].from, dateRanges['1M'].to, wanted('1M'));

  // 6M - Uses 4-hour data for better resolution. 3M is its last three months,
  // so it makes no request of its own.
  const q6M = useCompanyPrice4Hour(
    ticker,
    dateRanges['6M'].from,
    dateRanges['6M'].to,
    wanted('6M') || wanted('3M')
  );

  // YTD - Uses adaptive resolution based on days since Jan 1
  // All hooks called unconditionally to follow React rules, but only the
  // selected resolution is enabled - the other three never fetch
  const ytdResolution = getYTDResolution();
  const ytd = wanted('YTD');
  const qYTD_15min = useCompanyPrice15Min(ticker, dateRanges['YTD'].from, dateRanges['YTD'].to, ytd && ytdResolution === '15min');
  const qYTD_30min = useCompanyPrice30Min(ticker, dateRanges['YTD'].from, dateRanges['YTD'].to, ytd && ytdResolution === '30min');
  const qYTD_4hour = useCompanyPrice4Hour(ticker, dateRanges['YTD'].from, dateRanges['YTD'].to, ytd && ytdResolution === '4hour');
  const qYTD_daily = useCompanyPriceEod(ticker, dateRanges['YTD'].from, dateRanges['YTD'].to, ytd && ytdResolution === 'daily');

  // Select the appropriate YTD query based on resolution
  const getYTDQuery = () => {
    switch (ytdResolution) {
      case '15min': return qYTD_15min;
      case '30min': return qYTD_30min;
      case '4hour': return qYTD_4hour;
      default: return qYTD_daily;
    }
  };
  const qYTD = getYTDQuery();

  // 1Y - Uses EOD data (4-hour endpoint has ~6 month limit)
  const q1Y = useCompanyPriceEod(ticker, dateRanges['1Y'].from, dateRanges['1Y'].to, wanted('1Y'));

  // 5Y - Uses EOD data (4-hour endpoint has limited history)
  const q5Y = useCompanyPriceEod(ticker, dateRanges['5Y'].from, dateRanges['5Y'].to, wanted('5Y'));

  // 10Y - Uses EOD data (4-hour endpoint has limited history)
  const q10Y = useCompanyPriceEod(ticker, dateRanges['10Y'].from, dateRanges['10Y'].to, wanted('10Y'));

  // Build the result object with normalized data.
  // Deps reference the STABLE .data / primitive fields (not the query result
  // objects, which get a new identity every render in react-query v5) so the
  // returned object keeps its identity when nothing has actually changed.
  return useMemo(
    () => ({
      '1D': normalizeQuery(q1D, (data) =>
        data
          ? {
              fallbackUsed: data.fallbackUsed,
              actualDate: data.actualDate,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '1W': normalizeQuery(q1W, (data) =>
        data
          ? {
              fallbackUsed: data.fallbackUsed,
              actualDate: data.actualDate,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '1M': normalizeQuery(q1M, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '3M': normalizeQuery(
        {
          ...q6M,
          data: q6M.data?.data.filter((record) => record.date >= dateRanges['3M'].from),
        },
        (data) =>
          data
            ? {
                actualFrom: dateRanges['3M'].from,
                actualTo: q6M.data?.requestedTo,
                recordCount: data.length,
              }
            : undefined
      ),
      '6M': normalizeQuery(q6M, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      'YTD': normalizeQuery(qYTD, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '1Y': normalizeQuery(q1Y, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '5Y': normalizeQuery(q5Y, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
      '10Y': normalizeQuery(q10Y, (data) =>
        data
          ? {
              actualFrom: data.requestedFrom,
              actualTo: data.requestedTo,
              recordCount: data.recordCount,
            }
          : undefined
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      q1D.data, q1D.isLoading, q1D.isError, q1D.error,
      q1W.data, q1W.isLoading, q1W.isError, q1W.error,
      q1M.data, q1M.isLoading, q1M.isError, q1M.error,
      q6M.data, q6M.isLoading, q6M.isError, q6M.error,
      qYTD.data, qYTD.isLoading, qYTD.isError, qYTD.error,
      q1Y.data, q1Y.isLoading, q1Y.isError, q1Y.error,
      q5Y.data, q5Y.isLoading, q5Y.isError, q5Y.error,
      q10Y.data, q10Y.isLoading, q10Y.isError, q10Y.error,
    ]
  );
};
